import {
  ArrowLeftOutlined,
  BankOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  DatabaseOutlined,
  LinkOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Empty,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Tabs,
  Typography,
} from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Empty_State from "../../Assets/empty_state.png";
import { translation } from "../../i18n/translationConstant";
import FIPModel from "../../Models/FIPModel";
import { GetFIPRequest } from "../../Models/Request/FIP/GetAllFIP";
import { getFips } from "../../Services/FipServices/FipServices";
import {
  getAllUserActivity,
  getUserSpecificActivity,
} from "../../Services/UserActivityServices/UserActivity";
import { defaultPageSize } from "../../Utils/Constants/apiConstants";
import { ConsentStatusType } from "../../Utils/Constants/consentTypes";
import { QueryParams } from "../../Utils/Constants/routes";
import useINF from "../Common/Components/InfiniteScroll";
import CommonLayout from "../More/CommonLayout";
import { useFingerprintValidation } from "../Authentication/FingerprintValidationContext";
import "./index.css";

interface IUserActivityProps {
  isLoading: boolean;
}

const UserActivity: React.FC<IUserActivityProps> = ({ isLoading }) => {
  const { t } = useTranslation();
  const [userActivity, setUserActivity] = useState<any[]>([]);
  const [fip, setFIP] = useState<GetFIPRequest>([]);
  const pageSize = defaultPageSize;
  const [page, setPage] = useState(1);
  const [key, setKey] = useState("1");
  const history = useHistory();
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const observer = useRef<any>();
  const { newObserver, ref } = useINF(observer, hasMore, setPage);
  observer.current = newObserver.current;

  const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  };
  const query: any = useQuery();

  useEffect(() => {
    getFips()
      .then((res1) => res1.data)
      .then((res1: GetFIPRequest) => setFIP(res1));
  }, []);

  const getUserActivity = useCallback(() => {
    if (query.get(QueryParams.entityType)) {
      getUserSpecificActivity(
        page - 1,
        pageSize,
        query.get(QueryParams.entityId),
        query.get(QueryParams.entityType),
        query.get(QueryParams.fipId) ? query.get(QueryParams.fipId) : null
      )
        .then((res) => res.data)
        .then((res) => {
          setHasMore(!res.last);
          setUserActivity((old) => old.concat(res.content));
        });
    } else {
      getAllUserActivity(
        page - 1,
        pageSize,
        key === "2" ? "/consent" : key === "3" ? "/account" : ""
      )
        .then((res) => res.data)
        .then((res) => {
          setHasMore(!res.last);
          setUserActivity((old) => old.concat(res.content));
        });
    }
  }, [page, pageSize, query, key]);

  const { checkFingerprint } = useFingerprintValidation();

  useEffect(() => {
    getUserActivity();
    checkFingerprint();
  }, [getUserActivity, checkFingerprint]);

  const parseActivity = (jsonString: string) => {
    try {
      return JSON.parse(jsonString || "{}");
    } catch {
      return {};
    }
  };

  const activityTitle = (type: string, jsonString: string) => {
    const json = parseActivity(jsonString);

    switch (type) {
      case "AA_UPDATE_CONSENT_APPROVAL_STATUS":
      case "AA_UPDATE_CONSENT_APPROVAL_STATUS_ACCOUNT":
        return json.constentApprovalStatus ===
          (localStorage.getItem("isV1") === "true" ? "READY" : "APPROVED")
          ? t(translation.lblConsentApproved)
          : t(translation.lblConsentRejected);
      case "AA_UPDATE_CONSENT_STATUS":
      case "AA_UPDATE_CONSENT_STATUS_ACCOUNT":
        return json.consentStatusType === ConsentStatusType.ACTIVE
          ? t(translation.lblConsentApproved)
          : `${t(type)} ${json.consentStatusType || ""}`;
      case "AA_STORE_CREATE_CONSENT_REQUEST":
        return `${t(type)} by ${json.fiu?.name || ""}`;
      case "AA_STORE_ACCOUNT_LINK_UPDATE":
        return `${t(translation.account)} ${json.accountStatusType || ""} ${t(translation.success)}`;
      case "AA_FIP_ACCOUNT_DISCOVERY":
      case "ACCOUNT_DELINK":
      case "AA_STORE_SAVE_FI_REQUEST":
      case "AA_STORE_SAVE_FI_REQUEST_ACCOUNT":
        return t(type);
      case "AA_PURGE_FI_DATA_FETCHED":
      case "AA_PURGE_FI_DATA_FETCHED_ACCOUNT":
      case "AA_PURGE_FI_DATA_EXPIRE":
      case "AA_PURGE_FI_DATA_EXPIRE_ACCOUNT":
        return "Data has been provided to the Financial Institution";
      default:
        return type;
    }
  };

  const getID = (type: string, jsonString: string) => {
    const json = parseActivity(jsonString);
    return (
      json.transactionId ||
      json.txnid ||
      json.consentHandle ||
      json.linkRefNumber ||
      "—"
    );
  };

  const getInstitution = (type: string, jsonString: string) => {
    const json = parseActivity(jsonString);
    return (
      json.fip?.name ||
      json.fiu?.name ||
      json.accounts?.[0]?.fipHandle ||
      "Financial Institution"
    );
  };

  const getActivityStatus = (type: string, jsonString: string) => {
    const json = parseActivity(jsonString);
    if (json.constentApprovalStatus) {
      return json.constentApprovalStatus === "REJECTED" ? "Rejected" : "Success";
    }
    if (json.consentStatusType) {
      if (String(json.consentStatusType).toUpperCase().includes("EXPIRE")) return "Expired";
      if (String(json.consentStatusType).toUpperCase().includes("REVOK")) return "Revoked";
      return "Success";
    }
    if (type === "ACCOUNT_DELINK") return "Success";
    if (type.indexOf("PURGE") >= 0) return "Completed";
    return "Success";
  };

  const getActivityIcon = (type: string, status: string) => {
    if (status === "Rejected") return <CloseCircleFilled />;
    if (status === "Expired" || status === "Revoked") return <ClockCircleOutlined />;
    if (type.indexOf("ACCOUNT") >= 0 || type.indexOf("LINK") >= 0) return <LinkOutlined />;
    if (type.indexOf("FIP") >= 0 || type.indexOf("DATA") >= 0 || type.indexOf("PURGE") >= 0) {
      return <DatabaseOutlined />;
    }
    return <CheckCircleFilled />;
  };

  const retrieveAuditBasedOnKey = (activekey: string) => {
    setPage(1);
    setKey(activekey);
    setUserActivity([]);
    setHasMore(true);
    setSearchText("");
    setStatusFilter("all");
    setTypeFilter("all");
  };

  const filteredActivities = userActivity.filter((activity) => {
    const title = activityTitle(activity.activityType, activity.activityJson);
    const institution = getInstitution(activity.activityType, activity.activityJson);
    const id = getID(activity.activityType, activity.activityJson);
    const status = getActivityStatus(activity.activityType, activity.activityJson);
    const searchable = `${title} ${institution} ${id} ${status}`.toLowerCase();

    const matchesSearch = searchable.includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === "all" || status.toLowerCase() === statusFilter;
    const matchesType =
      typeFilter === "all" ||
      (typeFilter === "consent" && activity.activityType.toLowerCase().includes("consent")) ||
      (typeFilter === "account" && (activity.activityType.toLowerCase().includes("account") || activity.activityType.toLowerCase().includes("fip")));

    return matchesSearch && matchesStatus && matchesType;
  });

  const timeLineRender = () => {
    if (userActivity.length === 0) {
      return (
        <Empty
          image={Empty_State}
          description={
            <Typography.Text className="historyEmptyText">
              {t(translation.lblNoRecentActivities)}
            </Typography.Text>
          }
        />
      );
    }

    if (filteredActivities.length === 0) {
      return (
        <Empty description="No activities match your filters." />
      );
    }

    return (
      <>
        <div className="historyList">
          {filteredActivities.map((activity: any, index: number) => {
            const status = getActivityStatus(activity.activityType, activity.activityJson);
            const institution = getInstitution(activity.activityType, activity.activityJson);
            const id = getID(activity.activityType, activity.activityJson);
            const title = activityTitle(activity.activityType, activity.activityJson);
            const logo =
              fip.find((FIP: FIPModel) => FIP.name === parseActivity(activity.activityJson)?.fip?.name)?.logoUrl ||
              parseActivity(activity.activityJson)?.fiu?.logoUrl;

            return (
              <div className="historyRow" key={`${activity.time}-${index}`}>
                <div className={`historyActivityIcon historyActivityIcon-${status.toLowerCase()}`}>
                  {getActivityIcon(activity.activityType, status)}
                </div>

                <div className="historyEventColumn">
                  <Typography.Text className="historyEventTitle">{title}</Typography.Text>
                  <Typography.Text className="historyInstitution">{institution}</Typography.Text>
                </div>

                <div className="historyDetailsColumn">
                  <Typography.Text className="historyDetailsLabel">Transaction ID</Typography.Text>
                  <Typography.Text className="historyDetailsValue">{id}</Typography.Text>
                </div>

                <div className="historyStatusColumn">
                  <span className={`historyStatus historyStatus-${status.toLowerCase()}`}>
                    {status}
                  </span>
                </div>

                <div className="historyDateColumn">
                  <Typography.Text className="historyDate">
                    {moment(activity.time).format("DD MMM YYYY")}
                  </Typography.Text>
                  <Typography.Text className="historyTime">
                    {moment(activity.time).format("LT")}
                  </Typography.Text>
                </div>

                <div className="historyActionColumn">
                  {logo ? (
                    <Avatar className="historyMiniAvatar" src={logo} />
                  ) : (
                    <Avatar className="historyMiniAvatar" icon={<BankOutlined />} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {hasMore && <div className="infiniteDiv" ref={ref} />}
      </>
    );
  };

  const goBack = () => history.goBack();
  const { TabPane } = Tabs;

  return (
    <CommonLayout
      title={t(translation.lblHistory)}
      showBackButton={true}
    >
      <div className="historyRoot">
        <Typography.Text className="historySubtitle">
          View your consent and data access activity.
        </Typography.Text>

        {query.get(QueryParams.entityType) ? (
          <div className="historyContent">{timeLineRender()}</div>
        ) : (
          <>
            <div className="historyToolbarTop">
              <Tabs
                onChange={retrieveAuditBasedOnKey}
                animated={false}
                activeKey={key}
                className="historyTab"
              >
                <TabPane tab={t(translation.lblGeneral)} key="1" />
                <TabPane tab={t(translation.lblConsent)} key="2" />
                <TabPane tab={t(translation.lblAccounts)} key="3" />
              </Tabs>

              <Button className="historyDateButton" icon={<CalendarOutlined />}>
                {moment().startOf("month").format("D MMM YYYY")} – {moment().format("D MMM YYYY")}
              </Button>
            </div>

            <div className="historyFilters">
              <Input
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
                placeholder="Search by bank, transaction ID or status..."
                className="historySearch"
              />

              <Select value={typeFilter} onChange={setTypeFilter} className="historyFilterSelect">
                <Select.Option value="all">All Types</Select.Option>
                <Select.Option value="consent">Consent</Select.Option>
                <Select.Option value="account">Accounts</Select.Option>
              </Select>

              <Select value={statusFilter} onChange={setStatusFilter} className="historyFilterSelect">
                <Select.Option value="all">All Status</Select.Option>
                <Select.Option value="success">Success</Select.Option>
                <Select.Option value="expired">Expired</Select.Option>
                <Select.Option value="rejected">Rejected</Select.Option>
                <Select.Option value="revoked">Revoked</Select.Option>
              </Select>
            </div>

            <div className="historyTableHeader">
              <span>Event</span>
              <span>Details</span>
              <span>Status</span>
              <span>Date &amp; Time</span>
              <span></span>
            </div>

            <div className="historyContent">{timeLineRender()}</div>
          </>
        )}
      </div>
    </CommonLayout>
  );
};

const mapStateToProps = (state: any) => ({
  fip: state.fip.fips,
  isLoading: state.loader.searchLoader,
});

export default connect(mapStateToProps)(UserActivity);
